/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled, { css } from 'styled-components'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventUserCreate, AnalyticsEventWebappSignupDialogSuccessfulSignUp } from 'lib/constants/AnalyticsEventType'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { getReferrerScreen } from 'lib/formatting/url'
import { useRouter } from 'next/router'
import { parseLocale } from 'lib/constants/localization'
import { legalPrivacyRoute, legalTOSRoute, proPlanOptionRoute, signInRoute } from 'lib/constants/routes'
import {
    AdditionalSignUpAttributes,
    getDefaultAdditionalSignUpAttributes,
    LoginSteps,
} from 'lib/tracking/attribution/signupattributes'
import Stack from 'components/elements/Stack'
import { parseTranslationInterpolation } from 'lib/features/translation'
import { useToggleState } from 'lib/hooks/useToggleState'
import { Body2 } from 'stories/elements/Typography/Text'
import AuthEmailStepByStep from '../Authentication/AuthEmailStepByStep'

const Container = styled.div`
    background-color: ${theme.colors.white};
    height: 100%;
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    height: 100%;
    @media (max-width: 1050px) {
        height: auto;
        h1 {
            font-size: 16px !important;
        }
    }
`

const GridContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 25px 0;
    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
        padding: 0;
    }
    @media (max-width: 750px) {
        padding: 0;
        margin: 0;
    }

    .options {
        display: grid;
        align-items: center;
        justify-content: center;
        gap: 0px;
        grid-template-columns: 400px;
        padding-bottom: 15px;
        @media (max-width: 900px) {
            grid-template-columns: 250px;
        }
    }
    p {
        max-width: 400px;
        @media (max-width: 900px) {
            max-width: 100%;
        }
    }

    span {
        transition: all 1.5s ease;
        animation: showEmailButton 1.5s 1 ease-in-out forwards;
        @keyframes showEmailButton {
            0% {
                opacity: 0;
            }
            50% {
                opacity: 0;
            }

            100% {
                opacity: 1;
            }
        }
    }

    a {
        color: ${theme.colors.body1Black};
    }

    && svg {
        fill: ${theme.colors.body2Black};
    }
    .email-container,
    .password-container {
        grid-template-columns: 400px;
        gap: 12.5px;
        padding-bottom: 35px;
        position: relative;
        left: 0px;
        @media (max-width: 750px) {
            grid-template-columns: 1fr;
        }
    }
    .google-container {
        padding: 0;
    }
    .email-banner {
        display: flex;
        justify-content: center;
        input {
            border-radius: ${theme.borderRadius.small} !important;
            border: none !important;
            position: relative;
            bottom: 1px;
        }
        button {
            div {
                height: 45px;
                width: 100%;
            }
        }
    }
    .inputs {
        * {
            color: ${theme.colors.white};
        }
        gap: 15px;
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
`

const LegalArticleLink = styled.a<{ accent?: boolean }>`
    && {
        font-size: 12px !important;
        font-weight: 600;
        cursor: pointer;
        font-family: var(--font-inter);

        ${({ accent }) =>
            accent
                ? css`
                      font-size: 14px !important;
                      color: ${theme.colors.deepSeaBlue};
                  `
                : ''}
    }
`

const PlusPlanCheckoutLogin = () => {
    const [additionalSignUpAttributes, setAdditionalSignUpAttributes] = useState<AdditionalSignUpAttributes>()
    const [showEmailBannerFlowElement, toggleEmailBannerFlowElement] = useToggleState(false)
    const { t: tAuth } = _useTranslation('auth')
    const { t: tCommon } = _useTranslation('common')
    const { t: tKnows } = _useTranslation('knows')
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const referrerScreen = getReferrerScreen(router)

    useEffect(() => {
        const attributes = getDefaultAdditionalSignUpAttributes(parsedLocale.countryCode, parsedLocale.interfaceLanguageCode)

        setAdditionalSignUpAttributes(attributes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSuccessSignUp = (isNewAccount: boolean, type: 'google' | 'apple' | 'email') => {
        if (isNewAccount) {
            trackEvent(AnalyticsEventUserCreate, { value: type, referrerScreen })
            toast.success(tAuth('auth/signUpRegisterSuccessMessage'))
        }

        trackEvent(AnalyticsEventWebappSignupDialogSuccessfulSignUp, { referrerScreen })
    }

    return (
        <Container>
            <Wrapper>
                <GridContainer>
                    <Stack gutter={10}>
                        <div className="email-banner">
                            {additionalSignUpAttributes ? (
                                <AuthEmailStepByStep
                                    type={LoginSteps.SignUpEmail}
                                    additionalSignUpAttributes={additionalSignUpAttributes}
                                    onClick={() => {}}
                                    onSuccess={(isNewAccount) => onSuccessSignUp(isNewAccount, 'email')}
                                    removeMargins
                                    isSmall
                                    buttonText={tCommon('common/createFreeAccount')}
                                />
                            ) : null}
                            <span />
                        </div>

                        <Stack gutter={30}>
                            {showEmailBannerFlowElement ? (
                                <Body2
                                    textAlign="center"
                                    color={theme.colors.body1Black}
                                    fontFamily="var(--font-inter)"
                                    fontWeight={400}
                                    fontSize={12}
                                    style={{ cursor: 'pointer' }}
                                    onClick={toggleEmailBannerFlowElement}
                                >
                                    {tCommon('common/signUpWithGoogleButton')}
                                </Body2>
                            ) : (
                                <Body2
                                    textAlign="center"
                                    color={theme.colors.body1Black}
                                    fontFamily="var(--font-inter)"
                                    fontWeight={400}
                                    fontSize={12}
                                >
                                    {parseTranslationInterpolation(tKnows('knows/signUpRegisterLegalMessage'), [
                                        { index: 1, component: LegalArticleLink, props: { href: legalTOSRoute } },
                                        { index: 3, component: LegalArticleLink, props: { href: legalPrivacyRoute } },
                                    ])}
                                </Body2>
                            )}
                            <Body2 textAlign="center" color={theme.colors.deepSeaBlue}>
                                <LegalArticleLink
                                    accent
                                    href={signInRoute({
                                        referrerScreen,
                                        redirect: proPlanOptionRoute({ section: 'checkout' }),
                                    })}
                                >
                                    {tAuth('auth/signUpAlreadyHaveAnAccount')}
                                </LegalArticleLink>
                            </Body2>
                        </Stack>
                    </Stack>
                </GridContainer>
            </Wrapper>
        </Container>
    )
}

export default PlusPlanCheckoutLogin
